/**
 * A seção Servidor da administração (docs/spec/admin.md): a configuração em leitura. Tudo muda no
 * `apps/server/.env`, com o servidor reiniciado; segredo nenhum chega aqui.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../api.js';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import type { AdminServerInfo } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { Spinner } from '../ui/controls.js';
import { useDateTimeText } from './AdminLog.js';

export function AdminServer({ version }: { version: number }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const when = useDateTimeText();
  const [info, setInfo] = useState<AdminServerInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.adminServer(token).then(
      (result) => {
        if (!alive) return;
        setInfo(result);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  if (error) {
    return (
      <p className="rd-adminUsers__error" role="alert">
        {error}
      </p>
    );
  }
  if (!info) {
    return (
      <div className="rd-placeholder">
        <Spinner size={28} />
      </div>
    );
  }
  const list = (values: readonly string[]) => (values.length ? values.join(', ') : t('admin.server.none'));
  const yes = (value: boolean) => t(value ? 'admin.server.on' : 'admin.server.off');
  return (
    <section className="rd-adminServer" aria-label={t('admin.section.server')}>
      <p className="rd-adminPlans__hint">{t('admin.server.hint')}</p>
      <Group title={t('admin.server.running')}>
        <Fact label={t('admin.server.version')} value={info.version} />
        <Fact label={t('admin.server.node')} value={info.node} />
        <Fact label={t('admin.server.startedAt')} value={when(info.startedAt)} />
      </Group>
      <Group title={t('admin.server.limits')}>
        <Fact label={t('admin.server.maxUpload')} value={formatBytes(info.limits.maxUploadBytes)} env="RPGDESK_MAX_UPLOAD_BYTES" />
        <Fact
          label={t('admin.server.uploadMemory')}
          value={t('admin.server.inUseOf', { used: formatBytes(info.limits.uploadMemoryInUse), limit: formatBytes(info.limits.uploadMemoryBytes) })}
          env="RPGDESK_UPLOAD_MEMORY_BYTES"
        />
        <Fact label={t('admin.server.maxBody')} value={formatBytes(info.limits.maxBodyBytes)} env="RPGDESK_MAX_BODY_BYTES" />
        <Fact label={t('admin.server.history')} value={t('admin.server.messages', { n: info.limits.historyLimit })} env="RPGDESK_HISTORY_LIMIT" />
      </Group>
      <Group title={t('admin.server.access')}>
        <Fact label={t('admin.server.admins')} value={list(info.admins)} env="RPGDESK_ADMINS" />
        <Fact label={t('admin.server.cors')} value={list(info.corsOrigins)} env="RPGDESK_CORS_ORIGINS" />
        <Fact label={t('admin.server.plugins')} value={info.pluginsUrl ?? t('admin.server.samePlace')} env="RPGDESK_PLUGINS_URL" />
        <Fact label={t('admin.server.storage')} value={info.storagePrefix ?? t('admin.server.none')} env="RPGDESK_R2_PREFIX" />
        <Fact label={t('admin.server.randomOrg')} value={yes(info.randomOrg)} env="RPGDESK_RANDOM_ORG_API_KEY" />
        <Fact label={t('admin.server.client')} value={yes(info.servesClient)} env="RPGDESK_CLIENT_DIR" />
      </Group>
    </section>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <h3 className="rd-adminOverview__group">{title}</h3>
      <dl className="rd-adminServer__facts">{children}</dl>
    </>
  );
}

/** Um valor, com a variável do `.env` que o muda. */
function Fact({ label, value, env }: { label: string; value: string; env?: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <span>{value}</span>
        {env ? <code>{env}</code> : null}
      </dd>
    </>
  );
}
